import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  FileCheck,
  Upload,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { LOGOS } from "../../lib/ablemp-data";

const PF_DOCS = [
  {
    id: "documento_identificacao",
    title: "Documento de identificação",
    hint: "RG, CNH ou Passaporte (frente e verso em um único PDF, se possível).",
  },
  {
    id: "comprovante_endereco",
    title: "Comprovante de endereço",
    hint: "Conta de luz, água, telefone ou similar — emitido nos últimos 90 dias.",
  },
];

const PJ_DOCS = [
  {
    id: "contrato_social",
    title: "Contrato Social",
    hint: "Última versão consolidada / Estatuto registrado.",
  },
  {
    id: "documento_representante",
    title: "Documento do representante legal",
    hint: "RG, CNH ou Passaporte do administrador / sócio responsável.",
  },
  {
    id: "comprovante_endereco",
    title: "Comprovante de endereço",
    hint: "Comprovante da sede da empresa — emitido nos últimos 90 dias.",
  },
];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [personType, setPersonType] = useState(null); // 'PF' | 'PJ'
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState({});
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    api
      .get("/portal/onboarding/status")
      .then((r) => {
        setStatus(r.data);
        if (r.data.person_type) setPersonType(r.data.person_type);
      })
      .catch(() => {});
  }, []);

  // If somehow already completed, leave
  useEffect(() => {
    if (status?.completed) navigate("/portal/dashboard", { replace: true });
  }, [status, navigate]);

  const requiredDocs = useMemo(
    () => (personType === "PJ" ? PJ_DOCS : personType === "PF" ? PF_DOCS : []),
    [personType]
  );

  const uploaded = status?.docs || {};

  const handleUpload = async (docId, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Arquivo excede 15MB");
      return;
    }
    const fd = new FormData();
    fd.append("doc_type", docId);
    fd.append("file", file);
    setUploading((u) => ({ ...u, [docId]: true }));
    try {
      await api.post("/portal/onboarding/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fresh = await api.get("/portal/onboarding/status");
      setStatus(fresh.data);
      toast.success("Documento enviado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading((u) => ({ ...u, [docId]: false }));
    }
  };

  const allDone =
    personType && requiredDocs.every((d) => uploaded[d.id]?.file_name);

  const finish = async () => {
    if (!allDone) return;
    setCompleting(true);
    try {
      await api.post("/portal/onboarding/complete", {
        person_type: personType,
      });
      await refresh();
      toast.success("Boas-vindas concluídas! Bem-vindo à ABLEMP.");
      navigate("/portal/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div
      data-testid="onboarding-page"
      className="min-h-screen bg-[#F4F4F0] bg-grain text-slate-900"
    >
      <header className="bg-[#0A0F14] text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <img src={LOGOS.white} alt="ABLEMP" className="h-12 w-auto" />
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">
              Boas-vindas
            </div>
            <div className="text-sm">{user?.name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 lg:py-14">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#475569]">
          Passo {personType ? "2 de 2" : "1 de 2"} — Cadastro inicial
        </div>
        <h1 className="mt-2 font-serif-display text-4xl lg:text-5xl leading-tight">
          {personType
            ? "Envie seus documentos"
            : "Você é Pessoa Física ou Jurídica?"}
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          {personType
            ? "Esses documentos ficam disponíveis para a administração da ABLEMP avaliar e arquivar. Aceitamos PDF, JPG ou PNG (até 15MB por arquivo)."
            : "Selecione o tipo para que possamos solicitar a documentação correta. Isso aparece apenas no seu primeiro acesso."}
        </p>

        {!personType && (
          <div
            data-testid="person-type-selector"
            className="mt-10 grid sm:grid-cols-2 gap-4"
          >
            <TypeCard
              testid="select-pf"
              icon={User}
              title="Pessoa Física"
              hint="Documento de identificação + comprovante de endereço."
              onClick={() => setPersonType("PF")}
              accent="#002776"
            />
            <TypeCard
              testid="select-pj"
              icon={Building2}
              title="Pessoa Jurídica"
              hint="Contrato Social + documento do representante legal + comprovante de endereço."
              onClick={() => setPersonType("PJ")}
              accent="#009B3A"
            />
          </div>
        )}

        {personType && (
          <>
            <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 border border-slate-300 rounded-sm bg-white">
              <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Tipo selecionado
              </span>
              <span className="font-medium text-sm">
                {personType === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}
              </span>
              <button
                data-testid="change-person-type"
                onClick={() => setPersonType(null)}
                className="ml-2 text-[11px] uppercase tracking-[0.22em] text-[#002776] hover:underline"
              >
                Trocar
              </button>
            </div>

            <ul className="mt-8 space-y-4">
              {requiredDocs.map((d) => {
                const ud = uploaded[d.id];
                const done = !!ud?.file_name;
                const busy = uploading[d.id];
                return (
                  <li
                    key={d.id}
                    data-testid={`doc-row-${d.id}`}
                    className={`bg-white border rounded-md p-5 transition-colors ${
                      done ? "border-emerald-200" : "border-slate-200"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`h-12 w-12 grid place-items-center rounded-sm shrink-0 ${
                          done
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {done ? (
                          <CheckCircle2 size={22} strokeWidth={1.6} />
                        ) : (
                          <FileCheck size={22} strokeWidth={1.4} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-display text-xl leading-tight">
                          {d.title}
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{d.hint}</p>
                        {done && (
                          <div className="mt-2 text-xs text-emerald-700">
                            Enviado: {ud.file_name}
                          </div>
                        )}
                      </div>
                      <label
                        className={`inline-flex items-center gap-2 px-4 py-2.5 cursor-pointer text-[11px] uppercase tracking-[0.22em] ${
                          done
                            ? "border border-slate-300 hover:border-[#002776] text-slate-700"
                            : "bg-[#0A0F14] text-white hover:bg-[#002776]"
                        }`}
                      >
                        <Upload size={14} />
                        {busy ? "Enviando..." : done ? "Substituir" : "Enviar"}
                        <input
                          data-testid={`upload-${d.id}`}
                          type="file"
                          className="hidden"
                          accept="application/pdf,image/*"
                          disabled={busy}
                          onChange={(e) =>
                            handleUpload(d.id, e.target.files?.[0])
                          }
                        />
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-sm text-slate-600 max-w-xl">
                {allDone
                  ? "Tudo certo! Clique em concluir para acessar o seu painel."
                  : "Envie todos os documentos para concluir o cadastro."}
              </p>
              <button
                data-testid="onboarding-finish"
                onClick={finish}
                disabled={!allDone || completing}
                className="px-6 py-3 bg-[#0A0F14] text-white text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#002776] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completing ? "Finalizando..." : "Concluir e acessar painel"}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const TypeCard = ({ icon: Icon, title, hint, onClick, accent, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="text-left p-6 bg-white border border-slate-200 hover:border-[#0A0F14] hover:shadow-md rounded-md transition-all"
  >
    <div
      className="h-12 w-12 grid place-items-center rounded-sm"
      style={{ background: accent + "14", color: accent }}
    >
      <Icon size={22} strokeWidth={1.5} />
    </div>
    <div className="mt-5 font-serif-display text-2xl leading-tight">
      {title}
    </div>
    <p className="mt-2 text-sm text-slate-600">{hint}</p>
  </button>
);
